import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { db } from "../firebase";
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import "./SearchResults.css";
import "./Home.css";
import { trackPageView, trackProductView } from "../utils/analytics";

export default function SearchResults() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedNames, setExpandedNames] = useState(new Set());
  const [isMobile, setIsMobile] = useState(window.innerWidth < 769);
  const [currentPage, setCurrentPage] = useState(1);
  const [isTransitioning, setIsTransitioning] = useState(false);
  
  const queryParam = searchParams.get('q') || '';
  
  // Products per page based on screen size (same as Products page)
  const productsPerPage = isMobile ? 10 : 15;

  useEffect(() => {
    setSearchQuery(queryParam);
    searchProducts(queryParam);
    setCurrentPage(1); // Reset to first page on new search
    // Track search results page view
    trackPageView('search_results', `Search Results: ${queryParam}`);
  }, [queryParam]);

  useEffect(() => {
    const handleResize = () => {
      const newIsMobile = window.innerWidth < 769;
      if (newIsMobile !== isMobile) {
        setIsMobile(newIsMobile);
        setCurrentPage(1); // Reset to first page when screen size changes
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobile]);

  // Calculate pagination values
  const totalPages = Math.ceil(products.length / productsPerPage);
  const startIndex = (currentPage - 1) * productsPerPage;
  const endIndex = startIndex + productsPerPage;
  const currentProducts = products.slice(startIndex, endIndex);

  // Pagination handlers
  const goToPage = (page) => {
    if (page === currentPage || isTransitioning) return;
    
    setIsTransitioning(true);
    
    // Smooth scroll to show navbar and page header
    const scrollToTop = () => {
      const pageHeader = document.querySelector('.results-header');
      const navbar = document.querySelector('.navbar') || document.querySelector('nav');
      
      if (pageHeader) {
        // Calculate offset to show navbar + some padding
        const navbarHeight = navbar ? navbar.offsetHeight : 0;
        const offset = navbarHeight + 20; // Add 20px padding
        const elementPosition = pageHeader.offsetTop - offset;
        
        window.scrollTo({ 
          top: Math.max(0, elementPosition), 
          behavior: 'smooth' 
        });
      } else {
        // Fallback to top of page
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    };
    
    scrollToTop();
    
    // Add a small delay for smooth transition effect
    setTimeout(() => {
      setCurrentPage(page);
      setIsTransitioning(false);
    }, 300);
  };

  // Toggle product name expansion
  const toggleNameExpansion = (productId, event) => {
    event.stopPropagation(); // Prevent card click from triggering
    setExpandedNames(prev => {
      const newSet = new Set(prev);
      if (newSet.has(productId)) {
        newSet.delete(productId);
      } else {
        newSet.add(productId);
      }
      return newSet;
    });
  };

  // Check if text is truncated (more than 2 lines)
  const isTextTruncated = (text) => {
    const wordsPerLine = isMobile ? 3 : 4; // Approximate words per line
    const words = text.split(' ');
    return words.length > (wordsPerLine * 2); // More than 2 lines worth
  };

  // Highlight search keywords in product names
  const highlightKeywords = (text, searchTerm) => {
    if (!searchTerm || !searchTerm.trim()) {
      return text;
    }

    const regex = new RegExp(`(${searchTerm.trim()})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => {
      if (part.toLowerCase() === searchTerm.toLowerCase()) {
        return (
          <span key={index} className="highlight-keyword">
            {part}
          </span>
        );
      }
      return part;
    });
  };

  const searchProducts = async (searchTerm) => {
    setLoading(true);
    console.log('Searching for:', searchTerm);
    try {
      const productsRef = collection(db, "products");
      let productsQuery;
      
      if (searchTerm && searchTerm.trim()) {
        // Search by name (case-insensitive contains)
        const lowerQuery = searchTerm.toLowerCase();
        console.log('Lower query:', lowerQuery);
        productsQuery = query(productsRef, orderBy("name"));
        const querySnapshot = await getDocs(productsQuery);
        console.log('Total docs found:', querySnapshot.size);
        
        const filteredProducts = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          console.log('Product name:', data.name, 'matches:', data.name && data.name.toLowerCase().includes(lowerQuery));
          if (data.name && data.name.toLowerCase().includes(lowerQuery)) {
            filteredProducts.push({ 
              id: doc.id, 
              ...data,
              availability: data.availability || "In stock"
            });
          }
        });
        console.log('Filtered products:', filteredProducts.length);
        setProducts(filteredProducts);
      } else {
        // Show all products if no search query
        console.log('No search term, showing all products');
        productsQuery = query(productsRef, orderBy("createdAt", "desc"));
        const querySnapshot = await getDocs(productsQuery);
        const allProducts = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          allProducts.push({ 
            id: doc.id, 
            ...data,
            availability: data.availability || "In stock"
          });
        });
        console.log('All products:', allProducts.length);
        setProducts(allProducts);
      }
    } catch (error) {
      console.error("Error searching products:", error);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="search-results-container">
      {/* Results Header */}
      <div className="results-header">
        {queryParam ? (
          <h2>Search results for "{queryParam}"</h2>
        ) : (
          <h2>All Products</h2>
        )}
        <p>{products.length} items found</p>
      </div>

      {/* Loading State */}
      {loading ? (
        <div className="loading-state">
          <p>Searching products...</p>
        </div>
      ) : (
        <>
          {/* Results Grid */}
          {products.length > 0 ? (
            <>
              <div className={`product-grid-mobile ${isTransitioning ? 'transitioning' : ''}`}>
                {currentProducts.map((product) => (
                  <div className="product-card-mobile" key={product.id}>
                    <div className="product-image-container">
                      <img src={product.image} alt={product.name} className="product-img-mobile" />
                    </div>
                    <div className="product-details">
                      <h3 
                        className={`product-name-mobile ${
                          expandedNames.has(product.id) 
                            ? 'expanded' 
                            : isTextTruncated(product.name) 
                              ? 'truncated' 
                              : ''
                        }`}
                        onClick={(e) => isTextTruncated(product.name) && toggleNameExpansion(product.id, e)}
                        style={{ 
                          cursor: isTextTruncated(product.name) ? 'pointer' : 'default'
                        }}
                        title={isTextTruncated(product.name) ? "Click to expand/collapse" : ""}
                      >
                        {highlightKeywords(product.name, queryParam)}
                      </h3>
                      <p className="product-price-mobile">{product.price}</p>
                      <div className="availability-container">
                        <span className="availability-label">Status</span>
                        <span className={`availability-status ${(product.availability === 'In stock' || !product.availability) ? 'in-stock' : 'sold-out'}`}>
                          {(product.availability === 'In stock' || !product.availability) ? 'In stock' : 'Sold out'}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="pagination-container">
                  <div className="pagination-controls">
                    <div className="page-numbers">
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                        <button
                          key={page}
                          className={`page-btn ${currentPage === page ? 'active' : ''}`}
                          onClick={() => goToPage(page)}
                        >
                          {page}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="no-results">
              <img src="/images/icons8-search-50.png" alt="No results" style={{width: 48, height: 48, opacity: 0.3}} />
              <h3>No products found</h3>
              <p>Try searching with different keywords</p>
              <button 
                className="view-all-btn"
                onClick={() => navigate('/search')}
              >
                View All Products
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
